import './styles/global.css'
import { ThemeProvider } from './context/ThemeContext'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { AdminProvider, useAdmin } from './context/AdminContext'
import { ToastProvider } from './context/ToastContext'
import { LanguageProvider } from './i18n/LanguageContext'
import Sidebar from './components/Sidebar'
import Tara from './components/Tara'
import Dashboard from './pages/Dashboard'
import Orders from './pages/Orders'
import Inventory from './pages/Inventory'
import Analytics from './pages/Analytics'
import Finance from './pages/Finance'
import Goals from './pages/Goals'
import Events from './pages/Events'
import Learn from './pages/Learn'
import SetupWizard from './pages/SetupWizard'
import AddOrder from './pages/AddOrder'
import OrderDetail from './pages/OrderDetail'
import AddProduct from './pages/AddProduct'
import ProductDetail from './pages/ProductDetail'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Settings from './pages/Settings'
import WebsiteBuilder from './pages/WebsiteBuilder'
import ShopPage from './pages/ShopPage'
import TrackOrder from './pages/TrackOrder'
import OrderHistory from './pages/OrderHistory'
import SellerDirectory from './pages/SellerDirectory'
import Pricing from './pages/Pricing'
import CustomerSignup from './pages/CustomerSignup'
import CustomerLogin from './pages/CustomerLogin'
import CustomerPortal from './pages/CustomerPortal'
import CustomerOrderForm from './pages/CustomerOrderForm'
import AdminLogin from './pages/AdminLogin'
import AdminDashboard from './pages/AdminDashboard'
import AdminSellers from './pages/AdminSellers'
import AdminHelp from './pages/AdminHelp'
import AdminVerifications from './pages/AdminVerifications'
import AdminUsers from './pages/AdminUsers'
import AdminFlags from './pages/AdminFlags'
import AdminAdSlots from './pages/AdminAdSlots'
import AdminSettings from './pages/AdminSettings'
import AdminReferrals from './pages/AdminReferrals'
import AdminAnalytics from './pages/AdminAnalytics'
import AdminGoals from './pages/AdminGoals'
import AdminFinance from './pages/AdminFinance'
import AdminBoosts from './pages/AdminBoosts'
import CRM from './pages/CRM'
import POS from './pages/POS'
import Invoices from './pages/Invoices'
import AdBoost from './pages/AdBoost'
import EmailCampaigns from './pages/EmailCampaigns'
import PaymentResult from './pages/PaymentResult'
import AIModelPhotos from './pages/AIModelPhotos'
import TaraPage from './pages/TaraPage'
import SellerProfile from './pages/SellerProfile'
import ContentStudio from './pages/ContentStudio'
import FacebookConnect from './pages/FacebookConnect'
import QuickOrderForm from './pages/Quickorderform'

function ProtectedLayout() {
  const { user, loading } = useAuth()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F7F9FC' }}>
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: '#2DD4BF', borderTopColor: 'transparent' }}></div>
        <div className="text-sm" style={{ color: '#6B7A90' }}>Loading...</div>
      </div>
    </div>
  )
  if (!user) return <Navigate to="/login" />
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#F0F2F5' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minWidth: 0 }}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/add" element={<AddOrder />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/inventory/add" element={<AddProduct />} />
          <Route path="/inventory/:id" element={<ProductDetail />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/goals" element={<Goals />} />
          <Route path="/events" element={<Events />} />
          <Route path="/crm" element={<CRM />} />
          <Route path="/pos" element={<POS />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/setup" element={<SetupWizard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/website" element={<WebsiteBuilder />} />
          <Route path="/web/:username" element={<ShopPage />} />
          <Route path="/grow/adboost" element={<AdBoost />} />
          <Route path="/grow/email" element={<EmailCampaigns />} />
          <Route path="/grow/aimodel" element={<AIModelPhotos />} />
          {/* Pricing inside app shell for logged-in sellers */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment/success" element={<PaymentResult type="success" />} />
          <Route path="/payment/fail"    element={<PaymentResult type="fail" />} />
          <Route path="/payment/cancel"  element={<PaymentResult type="cancel" />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
          <Route path="/tara" element={<TaraPage />} />
          <Route path="/marketing/content-studio" element={<ContentStudio />} />
          <Route path="/marketing/fb-connect" element={<FacebookConnect />} />
          <Route path="/order/:slug" element={<QuickOrderForm />} />
        </Routes>
      </div>
      <Tara />
    </div>
  )
}

function AdminLayout() {
  const { admin } = useAdmin()
  if (!admin) return <Navigate to="/admin" />
  return (
    <Routes>
      <Route path="dashboard" element={<AdminDashboard />} />
      <Route path="sellers" element={<AdminSellers />} />
      <Route path="analytics" element={<AdminAnalytics />} />
      <Route path="goals" element={<AdminGoals />} />
      <Route path="finance" element={<AdminFinance />} />
      <Route path="help" element={<AdminHelp />} />
      <Route path="verifications" element={<AdminVerifications />} />
      <Route path="users" element={<AdminUsers />} />
      <Route path="flags" element={<AdminFlags />} />
      <Route path="ads" element={<AdminAdSlots />} />
      <Route path="settings" element={<AdminSettings />} />
      <Route path="referrals" element={<AdminReferrals />} />
      <Route path="adboost" element={<AdminBoosts />} />
      <Route path="*" element={<Navigate to="/admin/dashboard" />} />
    </Routes>
  )
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
      <AdminProvider>
        <BrowserRouter>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/shop/:username" element={<ShopPage />} />
              <Route path="/web/:username" element={<ShopPage />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/track/history" element={<OrderHistory />} />
              <Route path="/sellers" element={<SellerDirectory />} />
              <Route path="/sellers/:username" element={<SellerProfile />} />
              {/* Public pricing — for logged-out visitors from marketing site */}
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/customer/signup" element={<CustomerSignup />} />
              <Route path="/customer/login" element={<CustomerLogin />} />
              <Route path="/customer/portal" element={<CustomerPortal />} />
              <Route path="/order-form/:token" element={<CustomerOrderForm />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/*" element={<AdminLayout />} />
              <Route path="/*" element={<ProtectedLayout />} />
            </Routes>
          </ToastProvider>
        </BrowserRouter>
      </AdminProvider>
    </LanguageProvider>
    </ThemeProvider>
  )
}

export default App